/**
 * SystemHealthWidget — Fase A Afronding (22 Feb 2026).
 *
 * Toont per subsystem de status (healthy / warning / error) met bericht +
 * details. Gebruikt de nieuwe `/api/admin/system-health` endpoint dat
 * MongoDB, Supervisor, Resend webhook, Stripe webhook, Reminder Scheduler,
 * Tracking Updater en E-mail delivery aggregeert.
 *
 * Design:
 *  - Grid van status-kaarten (2 kolommen op medium, 1 op mobiel).
 *  - Duidelijke kleurindicator (🟢 / 🟡 / 🔴) via icon + border.
 *  - Klikbaar om details uit te klappen.
 *  - Refresh-knop rechtsboven.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2, AlertTriangle, XCircle, RefreshCw, Loader2,
  Database, Server, Bell, Radar, Mail, Zap, CreditCard, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { AdminSkeleton } from "../ui";

const COMPONENT_ICONS = {
  mongodb:            Database,
  supervisor:         Server,
  resend_webhook:     Mail,
  stripe_webhook:     CreditCard,
  reminder_scheduler: Bell,
  tracking_updater:   Radar,
  email_delivery:     Mail,
};

const STATUS_META = {
  healthy: {
    icon: CheckCircle2,
    dot: "🟢",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    ring: "ring-emerald-100",
    text: "text-emerald-800",
    subtext: "text-emerald-700",
    label: "Healthy",
  },
  warning: {
    icon: AlertTriangle,
    dot: "🟡",
    bg: "bg-amber-50",
    border: "border-amber-200",
    ring: "ring-amber-100",
    text: "text-amber-800",
    subtext: "text-amber-700",
    label: "Warning",
  },
  error: {
    icon: XCircle,
    dot: "🔴",
    bg: "bg-red-50",
    border: "border-red-200",
    ring: "ring-red-100",
    text: "text-red-800",
    subtext: "text-red-700",
    label: "Error",
  },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      dateStyle: "short", timeStyle: "short",
    });
  } catch { return iso; }
}

function fmtUptime(secs) {
  if (secs == null) return "—";
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  if (secs < 86400) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${h}u ${m}m`;
  }
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  return `${d}d ${h}u`;
}

export default function SystemHealthWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/system-health");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e, "Systeemstatus laden mislukt"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  const overall = data?.overall || "healthy";
  const overallMeta = STATUS_META[overall] || STATUS_META.healthy;
  const OverallIcon = overallMeta.icon;

  return (
    <div className="space-y-3" data-testid="system-health-widget">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <OverallIcon className={`w-5 h-5 ${overallMeta.text}`} />
          <div>
            <div className="text-sm font-semibold text-slate-800">
              Platform: <span className={overallMeta.text}>{overallMeta.label}</span>
            </div>
            <div className="text-xs text-slate-500">
              Backend uptime: {fmtUptime(data?.backend_uptime_seconds)} · Laatste check {fmtDate(data?.as_of)}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-slate-200 hover:bg-slate-50 transition-colors disabled:opacity-50"
          data-testid="system-health-refresh"
        >
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Ververs
        </button>
      </div>

      {loading ? (
        <AdminSkeleton variant="lines" rows={4} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {(data?.components || []).map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.healthy;
            const Icon = COMPONENT_ICONS[c.component] || Zap;
            const StatusIcon = meta.icon;
            const isExpanded = expanded === c.component;
            const hasDetails = c.details && Object.keys(c.details).length > 0;
            return (
              <div
                key={c.component}
                className={`border rounded-xl p-3 ${meta.bg} ${meta.border} transition-all ${
                  hasDetails ? "cursor-pointer hover:ring-2" : ""
                } ${isExpanded ? `ring-2 ${meta.ring}` : ""}`}
                onClick={() => hasDetails && setExpanded(isExpanded ? null : c.component)}
                data-testid={`system-health-card-${c.component}`}
                role={hasDetails ? "button" : undefined}
                tabIndex={hasDetails ? 0 : undefined}
              >
                <div className="flex items-start gap-2.5">
                  <div className={`w-8 h-8 rounded-lg bg-white/70 border ${meta.border} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${meta.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-sm font-semibold ${meta.text}`}>{c.label}</span>
                      <span aria-hidden>{meta.dot}</span>
                    </div>
                    <div className={`text-xs ${meta.subtext} mt-0.5`}>{c.message}</div>
                  </div>
                  {hasDetails && (
                    <ChevronDown
                      className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </div>

                {isExpanded && hasDetails && (
                  <div className="mt-2 pt-2 border-t border-white/60 space-y-1 text-[11px]"
                       data-testid={`system-health-details-${c.component}`}>
                    {Object.entries(c.details).map(([k, v]) => {
                      if (v == null || v === "") return null;
                      if (Array.isArray(v)) {
                        return (
                          <div key={k}>
                            <span className={`font-semibold ${meta.text} uppercase text-[10px]`}>{k}:</span>
                            <div className="ml-2">
                              {v.slice(0, 5).map((item, i) => (
                                <div key={i} className={meta.subtext}>
                                  {typeof item === "object" ? JSON.stringify(item) : String(item)}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      if (typeof v === "object") {
                        return (
                          <div key={k}>
                            <span className={`font-semibold ${meta.text} uppercase text-[10px]`}>{k}:</span>
                            <pre className={`ml-2 ${meta.subtext} font-mono text-[10px] whitespace-pre-wrap`}>
                              {JSON.stringify(v, null, 2)}
                            </pre>
                          </div>
                        );
                      }
                      return (
                        <div key={k} className="flex gap-2">
                          <span className={`font-semibold ${meta.text} uppercase text-[10px] flex-shrink-0`}>{k}:</span>
                          <span className={`${meta.subtext} break-words`}>{String(v)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
